export interface ParsedGtfsId {
  readonly feedId: string;
  readonly id: string;
}

/**
 * Parses a GTFS ID string in the format `"FeedId:Id"` into its component parts.
 * Leading and trailing whitespace is trimmed from the input before parsing.
 *
 * @example
 * parseGtfsId('TriMet:769')    // { feedId: 'TriMet', id: '769' }
 * parseGtfsId(' TriMet:769 ') // { feedId: 'TriMet', id: '769' }
 * parseGtfsId('noColon')       // { feedId: '', id: 'noColon' }
 * parseGtfsId(null)            // null
 * parseGtfsId('')              // null
 * parseGtfsId(' ')             // null
 *
 * @returns A `ParsedGtfsId` object with:
 *   - `feedId`: the dataset/feed namespace portion before the first colon.
 *     In OTP, this value is assigned when the graph is built and stored in the graph,
 *     so a GTFS/NeTEx dataset keeps the same `feedId` after loading.
 *     If no colon is present, this function returns an empty string for `feedId`.
 *   - `id`: the entity-local identifier portion after the first colon.
 *     This is the ID component used across feed-scoped OTP `gtfsId` values
 *     (for example `agencyId`, `stopId`, `routeId`, `tripId`, etc.).
 *     If no colon is present, this function returns the full input as `id`.
 *
 *   Returns `null` if the input is `null`, `undefined`, or blank.
 */
export function parseGtfsId(
  gtfsId: string | null | undefined
): ParsedGtfsId | null {
  const trimmedId = gtfsId?.trim();
  if (!trimmedId) return null;
  const colonIndex = trimmedId.indexOf(':');
  if (colonIndex === -1) return { feedId: '', id: trimmedId };
  return {
    feedId: trimmedId.slice(0, colonIndex),
    id: trimmedId.slice(colonIndex + 1),
  };
}
